import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const SiteConfigContext = createContext({
  loading: true,
  configError: "",
  authEnabled: false,
  personalSiteMode: false,
  localAdminMode: false,
  authMode: "steam",
  appUrl: null,
  retrySiteConfig: () => {},
});

export const SiteConfigProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [configError, setConfigError] = useState("");
  const [authEnabled, setAuthEnabled] = useState(false);
  const [personalSiteMode, setPersonalSiteMode] = useState(false);
  const [localAdminMode, setLocalAdminMode] = useState(false);
  const [authMode, setAuthMode] = useState("steam");
  const [appUrl, setAppUrl] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      setLoading(true);
      setConfigError("");
      try {
        const { data } = await api.get("/site-config");
        if (!alive) return;
        setAuthEnabled(Boolean(data.auth_enabled));
        setPersonalSiteMode(Boolean(data.personal_site_mode));
        setLocalAdminMode(Boolean(data.local_admin_mode));
        setAuthMode(data.auth_mode || "steam");
        setAppUrl(data.app_url || null);
      } catch (error) {
        console.warn("Unable to load site config.", error);
        if (!alive) return;
        setAuthEnabled(false);
        setPersonalSiteMode(false);
        setLocalAdminMode(false);
        setAuthMode("steam");
        setAppUrl(null);
        setConfigError("Unable to reach site settings. Check your connection and retry.");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [retryKey]);

  const retrySiteConfig = () => setRetryKey((current) => current + 1);

  return (
    <SiteConfigContext.Provider value={{ loading, configError, authEnabled, personalSiteMode, localAdminMode, authMode, appUrl, retrySiteConfig }}>
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = () => useContext(SiteConfigContext);
